// ref: docs/CODEMAPS/PATTERNS.md §4.3 Hooks — Extracting UI Logic
import _ from 'lodash';
import { useMemo, useState } from 'react';
import type { IProps } from './types';

/**
 * Backs the custom text input with password visibility, value normalization and the effective input type. Toggles showPW from the eye icon, forwards digit-filtered changes through props.onChange, and memoizes the rendered type as text for number fields, the raw type for non-password fields, and text or password according to showPW.
 *
 * @param props - Field name, input type, current value and the change callback.
 * @returns The password visibility flag, eye icon and change handlers, and the resolved input type.
 */
export const useCustomInput = (props: Pick<IProps, 'name' | 'type' | 'value' | 'onChange'>) => {
  const { name = '' } = props;
  const [showPW, setShowPW] = useState(false);

  /**
   * Toggles the password field between masked and plaintext. Flips the showPW state flag.
   *
   * @returns Nothing.
   */
  const onClickEyeIcon = () => {
    setShowPW((prev) => !prev);
  };

  /**
   * Normalizes and forwards a value typed into the custom input. Strips every non-digit character when the input type is number, and calls props.onChange with the field name and value only when it differs from the current value.
   *
   * @param e - The input change event.
   * @returns Nothing.
   */
  const onChange = (e: any) => {
    let value = e.target?.value;

    if (props.onChange) {
      if (props.type === 'number') {
        value = _.replace(value, /[^0-9]/g, '');
      }
    }

    if (value !== props.value) {
      props.onChange?.(name, value);
    }
  };

  /**
   * Computes the effective HTML input type for the custom input based on the configured type and password visibility. Yields text for number inputs, the raw type for non-password inputs, and text or password for password inputs depending on the showPW flag.
   *
   * @returns The resolved input type.
   */
  const typeInput = useMemo(() => {
    if (props.type === 'number') {
      return 'text';
    }

    if (props.type !== 'password') {
      return props.type;
    }

    return showPW ? 'text' : 'password';
  }, [props.type, showPW]);

  return { showPW, onClickEyeIcon, onChange, typeInput };
};
